import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    // PP-920: cart add and cart update events share one event name. Updates
    // (quantity changes, line removals, the warranty pair cleanup, the re-sync
    // after returning from the checkout, the customer type switch) can arrive
    // moments after the customer closed the drawer and must never pop it open
    // again. Only a real add to cart opens the drawer; flows that need it open
    // afterwards call open() themselves (see warranty-addons.js).
    if (!(event instanceof CartAddEvent)) return;

    // PP-863: the warranty upsell drawer opens instead; the cart drawer follows
    // as soon as the upsell is answered (see warranty-addons.js).
    if (event?.detail?.data?.warrantyUpsellPending === true) return;

    this.showDialog();

    const didError = event?.detail?.data?.didError === true;
    const productHandle = event?.detail?.data?.productHandle;
    if (!didError && productHandle && typeof window.vslyEvent === 'function') {
      window.vslyEvent('atc_modal_shown', { productHandle });
    }
  };

  open() {
    this.showDialog();
    this.#syncWithCart();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * PP-987: makes sure an opened drawer shows the current cart. The drawer is
   * morphed from the section markup that cart requests carry along. Whenever that
   * markup did not reach it (a request without sections, a section render that
   * was answered late or from a cache, the header hydration of the page load
   * overlapping a cart change) the drawer would keep showing the previous cart.
   * A drawer that disagrees with the cart is re-rendered in place.
   */
  async #syncWithCart() {
    const sectionId = this.closest('.shopify-section')?.id?.replace('shopify-section-', '');
    if (!sectionId) return;

    try {
      const cart = await (await fetch('/cart.js')).json();
      if (this.#showsCart(cart)) return;

      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      // Own request: a render of the same section that is still pending from the
      // page load must not answer this one with its older markup.
      url.searchParams.set('cart_sync', String(Date.now()));
      await sectionRenderer.renderSection(sectionId, { cache: false, mode: 'hydration', url });
    } catch (error) {
      console.error('Cart drawer could not be synced with the cart:', error);
    }
  }

  /**
   * Whether the rendered drawer matches the cart: same item count and one row per
   * cart line, compared by the line item keys.
   * @param {{ item_count: number, items: { key: string }[] }} cart
   */
  #showsCart(cart) {
    const renderedCount = Number(
      this.querySelector('[ref="cartItemCount"]')?.textContent?.trim() ?? 0
    );
    if (renderedCount !== cart.item_count) return false;

    const renderedKeys = new Set(
      Array.from(this.querySelectorAll('.cart-items__table-row[data-key]')).map((row) =>
        row.getAttribute('data-key')
      )
    );
    return (
      renderedKeys.size === cart.items.length &&
      cart.items.every((item) => renderedKeys.has(item.key))
    );
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
